import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, TrendingDown, DollarSign, Calendar, 
  Loader2, Award, Clock, Briefcase, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Using api from lib/axios

const MemberBonusesPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bonuses, setBonuses] = useState([]);
  const [stats, setStats] = useState({
    totalBonus: 0,
    totalMalus: 0,
    netAmount: 0,
    projectCount: 0
  });

  useEffect(() => {
    fetchBonuses();
  }, []);

  const fetchBonuses = async () => {
    try {
      const response = await api.get('/ratings/member/${user.id}/bonuses');
      const data = response.data || {};
      const bonusesList = data.bonuses || [];
      setBonuses(bonusesList);
      
      // Use backend summary if available, otherwise calculate
      if (data.summary) {
        setStats({
          totalBonus: data.summary.total_bonus || 0,
          totalMalus: data.summary.total_malus || 0,
          netAmount: data.summary.net || 0,
          projectCount: data.summary.count || 0
        });
      } else {
        // Fallback calculation
        const totalBonus = bonusesList.filter(b => b.bonus_amount > 0).reduce((sum, b) => sum + b.bonus_amount, 0);
        const totalMalus = bonusesList.filter(b => b.bonus_amount < 0).reduce((sum, b) => sum + Math.abs(b.bonus_amount), 0);
        
        setStats({
          totalBonus,
          totalMalus,
          netAmount: totalBonus - totalMalus,
          projectCount: bonusesList.length
        });
      }
    } catch (error) {
      console.error('Error fetching bonuses:', error);
      toast.error('Erreur lors du chargement des primes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8 pt-32">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Mes Bonus & Malus
            </h1>
            <p className="text-slate-500 mt-1">
              Consultez le détail de vos primes basées sur les évaluations projets
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">+{stats.totalBonus.toFixed(2)}€</p>
                    <p className="text-xs text-slate-500">Total Bonus</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">-{stats.totalMalus.toFixed(2)}€</p>
                    <p className="text-xs text-slate-500">Total Malus</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className={cn(
                      "text-2xl font-bold",
                      stats.netAmount >= 0 ? "text-emerald-600" : "text-red-600"
                    )}>
                      {stats.netAmount >= 0 ? '+' : ''}{stats.netAmount.toFixed(2)}€
                    </p>
                    <p className="text-xs text-slate-500">Solde Net</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.projectCount}</p>
                    <p className="text-xs text-slate-500">Projets évalués</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Card */}
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 mb-1">Comment ça marche ?</p>
                  <ul className="text-blue-700 space-y-1">
                    <li>• <strong>Bonus +10%</strong> : Attribué quand un projet est noté ≥18/20 par le client</li>
                    <li>• <strong>Malus -10%</strong> : Appliqué si la note est &lt;13/20 et qu&apos;il y a eu du retard</li>
                    <li>• Ces ajustements sont calculés sur votre temps travaillé sur le projet</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bonuses List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : bonuses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune prime pour le moment</h3>
                <p className="text-slate-500">
                  Les primes seront calculées après les évaluations de vos projets par les clients
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bonuses.map((bonus, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Left: Project Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-lg text-slate-800">{bonus.project_name}</h3>
                          <Badge className={bonus.bonus_type === 'bonus' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {bonus.bonus_type === 'bonus' ? (
                              <><TrendingUp className="w-3 h-3 mr-1" />Bonus</>
                            ) : (
                              <><TrendingDown className="w-3 h-3 mr-1" />Malus</>
                            )}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Award className="w-4 h-4" />
                            Note: {bonus.final_rating}/20
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {bonus.total_hours}h travaillées
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(bonus.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                      
                      {/* Right: Amount */}
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-slate-500 mb-1">Valeur base</p>
                          <p className="font-medium text-slate-700">{bonus.total_value?.toFixed(2)}€</p>
                        </div>
                        <div className="text-right border-l pl-6">
                          <p className="text-xs text-slate-500 mb-1">Ajustement ({bonus.bonus_percentage}%)</p>
                          <p className={cn(
                            "text-2xl font-bold",
                            bonus.bonus_amount > 0 ? "text-emerald-600" : "text-red-600"
                          )}>
                            {bonus.bonus_amount > 0 ? '+' : ''}{bonus.bonus_amount?.toFixed(2)}€
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MemberBonusesPage;
